export const APP_IDS = [
  "revenue",
  "viralz",
  "tikwave",
  "gogrow",
  "rihla",
] as const;

export type AppId = (typeof APP_IDS)[number];

export const BUILDING_IDS = [
  "townHall",
  "barracks",
  "goldMine",
  "crystalTankRevenue",
  "crystalTankViralz",
  "crystalTankTikwave",
  "crystalTankGoGrow",
  "crystalTankRihla",
  "lab",
  "workshop",
  "watchtower",
  "walls",
  "builderHut",
] as const;

export type BuildingId = (typeof BUILDING_IDS)[number];

export const DECORATION_IDS = [
  "crescentLantern",
  "flowerBed",
  "fountain",
  "datePalm",
  "villageBanner",
  "gardenBench",
] as const;

export type DecorationId = (typeof DECORATION_IDS)[number];

export type BuilderFlags = {
  repoMade: boolean;
  firstDeploy: boolean;
  mvpLive: boolean;
  firstUser: boolean;
  firstPayingUser: boolean;
};

export type VillageMetrics = {
  date: string;
  planMilestonesHit: number;
  applicationsSent: number;
  interviewsLanded: number;
  offersReceived: number;
  sideHustleGbpPerMonth: number;
  appMrrUsd: Record<AppId, number>;
  totalMrrUsd: number;
  lessonsDone: number;
  builder: BuilderFlags;
  socialPosts: number;
  socialFollowers: number;
  bugsFixed: number;
  dailyActionsDoneYesterday: boolean;
  dailyActionsTotal: number;
};

export type BuildingProgress = {
  id: BuildingId;
  level: number;
  rawLevel: number;
  capped: boolean;
  value: number | boolean | string;
  nextThreshold: number | string | null;
  progressToNext: number;
};

export type RaidResult = {
  happened: boolean;
  goldStolen: number;
  crystalsStolen: number;
  wallDamage: number;
};

export type CollectResult = {
  happened: boolean;
  gold: number;
  crystals: number;
};

export type SprintState = {
  buildingId: BuildingId;
  startedAtMs: number;
  durationMs: number;
  completed: boolean;
  /** When the sprint was paused, or null/undefined while running. */
  pausedAtMs?: number | null;
  /** Total time spent paused before the current pause. */
  pausedMs?: number;
};

export type VillageState = {
  date: string;
  levels: Record<BuildingId, number>;
  gold: number;
  crystals: number;
  wallHp: number;
  decorations: DecorationId[];
  lastDailyActionsDone: boolean;
  justLeveled: BuildingId[];
  raid: RaidResult | null;
  collect: CollectResult | null;
  sprint: SprintState | null;
  bugsFixedSeen: number;
};

export type TickResult = {
  state: VillageState;
  buildings: BuildingProgress[];
  missingMetrics: string[];
};

export type ShopItem = {
  id: string;
  kind: "repair" | "decoration";
  name: string;
  description: string;
  goldCost: number;
  crystalCost: number;
  decorationId?: DecorationId;
  wallHp?: number;
  /** Repairs walls straight back to max HP. */
  restoreFull?: boolean;
};

export type ShopError = {
  ok: false;
  error: "cannot_afford" | "already_owned" | "unknown_item" | "walls_full";
};

export type ShopSuccess = {
  ok: true;
  state: VillageState;
  item: ShopItem;
};

export type ShopResult = ShopSuccess | ShopError;
